// ============================================================================
// The Analysis panel contract.
// ----------------------------------------------------------------------------
// A new panel is ONE FILE plus ONE ENTRY in ANALYSIS_PANELS. Nothing in
// AnalysisPage.tsx changes, ever. The shell reads only PanelDef + PanelSeries +
// Cell and never knows which panel it is drawing, so there is no panel-specific
// JSX anywhere in the tree.
//
// The load-bearing idea is the Cell. One object carries all three things a
// figure owes the operator:
//
//   select()  THE PROOF    — the exact rows behind this number
//   formula() THE WORKING  — the arithmetic, in words, over those rows
//   drill     THE DOOR     — where clicking it lands, and pre-filtered how
//
// The evidence drawer, the CSV writer, the PDF SVG emitter and the run-sheet
// handoff all consume that same object, which is why a figure on screen, a
// figure in the export and the row count in the drawer cannot disagree.
// ============================================================================

import type { DerivedRow, WindowStamp, Period, LaneKey } from './model';

export type { DerivedRow, WindowStamp, Period, LaneKey };

export type PanelSpan = 3 | 4 | 6 | 12;
export type PanelReadiness = 'live' | 'thin' | 'dormant';
export type PanelMark = 'matrix' | 'bars' | 'figures' | 'none';

export type PanelId =
    | 'ageing-matrix'
    | 'intake-volume'
    | 'trade-mix'
    | 'top-clients'
    | 'outcomes'
    | 'work-order-conversion';

export interface AnalysisContext {
    /** Period + free-text filter applied. What most panels read. */
    rows: DerivedRow[];
    /** Unfiltered. Only for panels declaring respectsPeriod: false. */
    allRows: DerivedRow[];
    now: number;
    window: WindowStamp;
    period: Period;
    query: string;
}

/**
 * THE DOOR. Where a click lands on the Run Sheet, and what is already filtered
 * when it gets there.
 *
 * `lane: null` means no honest handoff exists for this mark. It is not a
 * failure to fill in later: sending the operator to a lane that does not
 * actually contain these rows is worse than not offering the trip.
 */
export interface DrillTarget {
    lane: LaneKey | 'all' | null;
    query: string;
    sort: 'newest' | 'oldest' | 'value';
    exact: boolean;
    note?: string;
}

/** One clickable mark. Proof, working and door on the same object. */
export interface Cell {
    key: string;
    label: string;
    count: number;
    /** Ex GST. Always carried from DerivedRow.value, never recomputed. */
    value: number;
    /** 0..1. Drives fill length or tint depth. Never a colour decision. */
    weight: number;
    /** A lane tone var(), identity only. Never magnitude. */
    tone?: string;
    rowKey?: string;
    colKey?: string;
    /** THE PROOF: the exact rows behind this number. */
    select(rows: DerivedRow[]): DerivedRow[];
    /** THE WORKING LINE: the arithmetic in words, over the rows select() returned. */
    formula(picked: DerivedRow[]): string;
    /** THE DOOR. */
    drill: DrillTarget;
}

export interface PanelSeries {
    cells: Cell[];
    rowKeys?: { key: string; label: string; tone: string }[];
    colKeys?: { key: string; label: string }[];
    total: { count: number; value: number };
    /** Lead slot only. */
    headline?: { figure: string; caps: string; clause: string };
    /** Shown when cells.length === 0. A designed empty state, not an apology. */
    emptySeries?: string;
}

export interface PanelDef {
    id: PanelId;
    /** Authored sentence case; the CSS renders it uppercase. */
    title: string;
    /** Lowercase fragment, sits under the title. */
    hint: string;
    group: 'flow' | 'money' | 'work' | 'outcome';
    defaultSpan: PanelSpan;
    minSpan: PanelSpan;
    slim: boolean;
    mark: PanelMark;
    orientation?: 'row' | 'column';
    /**
     * false => the panel reads ctx.allRows and its head shows an "all time" chip,
     * so the operator never wonders why it did not move when the period changed.
     */
    respectsPeriod: boolean;

    compute(ctx: AnalysisContext): PanelSeries;
    readiness(s: PanelSeries, ctx: AnalysisContext): PanelReadiness;
    /** Non-null ONLY when the panel cannot yet do its job. Says what switches it on. */
    activation(s: PanelSeries, ctx: AnalysisContext): string | null;
    /** THE SIGNATURE: what this number is made of, printed under every panel. */
    stamp(s: PanelSeries, ctx: AnalysisContext): string;

    /** API field paths. The drawer renders these as a chip strip. */
    fields: string[];
    caveats?: string[];

    /**
     * THE POPULATION: the rows this panel's stamp census actually counts, given a
     * source set. `series.total` is already the population in all six panels;
     * this is its row-level twin.
     *
     * It exists because three panels count NARROWER than the set they are handed
     * (ageing-matrix counts only undecided rows, outcomes only sent-or-decided,
     * top-clients only named clients). Without it, opening the drawer from the
     * STAMP proved the whole source while rendering the stamp verbatim four rows
     * above, so one drawer showed two irreconcilable numbers and erred larger.
     *
     * Omit when every source row is in the population.
     */
    population?(rows: DerivedRow[]): DerivedRow[];

    csv(s: PanelSeries): { headers: string[]; rows: Record<string, string | number>[] };
    /** Static inline SVG. No script: the print document is served script-src 'none'. */
    printSvg(s: PanelSeries, w: number, h: number): string;
}

export interface SavedReportPanel {
    id: PanelId;
    span: PanelSpan;
}

export interface SavedReport {
    id: string;
    name: string;
    period: Period;
    panels: SavedReportPanel[];
    builtIn?: boolean;
}

import ageingMatrix from './panels/ageing-matrix';
import intakeVolume from './panels/intake-volume';
import tradeMix from './panels/trade-mix';
import topClients from './panels/top-clients';
import outcomes from './panels/outcomes';
import workOrderConversion from './panels/work-order-conversion';

export const ANALYSIS_PANELS: Record<PanelId, PanelDef> = {
    'ageing-matrix': ageingMatrix,
    'intake-volume': intakeVolume,
    'trade-mix': tradeMix,
    'top-clients': topClients,
    outcomes,
    'work-order-conversion': workOrderConversion,
};

/**
 * The board's default arrangement, in the order the operator reads it:
 * the money that is waiting first, then whether work is arriving, then what
 * kind of builder the book says you are, then concentration, then outcomes,
 * then the one honest announcement about the future.
 */
export const BUILTIN_REPORT: SavedReport = {
    id: 'builtin',
    name: 'The book',
    period: 'last12m',
    builtIn: true,
    panels: [
        { id: 'ageing-matrix', span: 12 },
        { id: 'intake-volume', span: 6 },
        { id: 'trade-mix', span: 6 },
        { id: 'top-clients', span: 6 },
        { id: 'outcomes', span: 6 },
        { id: 'work-order-conversion', span: 12 },
    ],
};

/**
 * Re-exported for callers that already reach for it here. Panels must import it
 * from ./model directly: importing a VALUE out of this module from a panel closes
 * a runtime cycle, because this module value-imports all six panels above.
 */
export { censusPhrase } from './model';
